import { useState, useEffect } from "react";
import { useApp, api } from "../context";
import { Shell, Btn, EmptyState } from "../components";

const WalletPage = () => {
  const { c, dark, user, flash, go, conversations, loadConversations, setViewingUser } = useApp();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [tab, setTab] = useState("wallet");
  const [loading, setLoading] = useState(true);
  const [sendTo, setSendTo] = useState("");
  const [sendAmount, setSendAmount] = useState("");
  const [sendMsg, setSendMsg] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [recipientSearch, setRecipientSearch] = useState("");
  const [recipientSearchResults, setRecipientSearchResults] = useState([]);
  const [searchingRecipient, setSearchingRecipient] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [favoritesLoaded, setFavoritesLoaded] = useState(false);
  const [redeemType, setRedeemType] = useState("airtime");
  const [redeemTokens, setRedeemTokens] = useState("");
  const [redeemPhone, setRedeemPhone] = useState("");
  const [redeemNetwork, setRedeemNetwork] = useState("Safaricom");
  const [buyPhone, setBuyPhone] = useState("");
  const [buyingPkg, setBuyingPkg] = useState(null);
  const [buyLoading, setBuyLoading] = useState(false);
  const [buySent, setBuySent] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Rewards & Referrals state
  const [rewards, setRewards] = useState(null);
  const [rewardHistory, setRewardHistory] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => { loadWallet(); }, []);

  useEffect(() => {
    if (tab === "rewards") loadRewards();
    if (tab === "referrals") loadReferrals();
    if (tab === "send") {
      if (!favoritesLoaded) loadFavorites();
      if (!conversations || conversations.length === 0) loadConversations?.();
    }
  }, [tab]); // eslint-disable-line

  const loadFavorites = async () => {
    const r = await api("/wallet/favorites");
    if (r.ok) setFavorites(r.data?.favorites || []);
    setFavoritesLoaded(true);
  };

  const toggleFavorite = async (u) => {
    const existing = favorites.find(f => f.id === u.id);
    if (existing) {
      await api(`/wallet/favorites/${existing.favorite_id}`, { method: "DELETE" });
      setFavorites(prev => prev.filter(f => f.id !== u.id));
      flash("Removed from favorites");
    } else {
      const r = await api("/wallet/favorites", { method: "POST", body: JSON.stringify({ user_id: u.id }) });
      if (r.ok) {
        setFavorites(prev => [{ favorite_id: r.data.favorite_id, id: u.id, name: u.name, avatar_url: u.avatar_url || u.participantPic || null, emoji: u.emoji || "👤", plan: u.plan || "free", verified: u.verified || false }, ...prev]);
        flash("Added to favorites ⭐");
      } else flash(r.message || "Failed", "error");
    }
  };

  useEffect(() => {
    if (!recipientSearch.trim()) { setRecipientSearchResults([]); return; }
    setSearchingRecipient(true);
    const timer = setTimeout(async () => {
      const r = await api(`/search?q=${encodeURIComponent(recipientSearch.trim())}`);
      if (r.ok) setRecipientSearchResults((r.data?.users || []).filter(u => String(u.id) !== String(user?.id)));
      setSearchingRecipient(false);
    }, 350);
    return () => clearTimeout(timer);
  }, [recipientSearch]); // eslint-disable-line

  const loadWallet = async () => {
    setLoading(true);
    const r = await api("/wallet");
    if (r.ok) {
      setWallet(r.data.wallet);
      setTransactions(r.data.transactions || []);
      setPackages(r.data.packages || []);
    }
    setLoading(false);
  };

  const loadRewards = async () => {
    setRewardsLoading(true);
    const [sumR, histR] = await Promise.all([api("/rewards/summary"), api("/rewards/history")]);
    if (sumR.ok) setRewards({ ...sumR.data, gt_balance: parseFloat(sumR.data.gt_balance || 0), total_earned: parseFloat(sumR.data.total_earned || 0), referral_earned: parseFloat(sumR.data.referral_earned || 0), login_streak: parseInt(sumR.data.login_streak || 0), referral_count: parseInt(sumR.data.referral_count || 0) });
    if (histR.ok) setRewardHistory(histR.data.data || []);
    setRewardsLoading(false);
  };

  const loadReferrals = async () => {
    setRewardsLoading(true);
    const [sumR, refR] = await Promise.all([api("/rewards/summary"), api("/rewards/referrals")]);
    if (sumR.ok) setRewards({ ...sumR.data, gt_balance: parseFloat(sumR.data.gt_balance || 0), total_earned: parseFloat(sumR.data.total_earned || 0), referral_earned: parseFloat(sumR.data.referral_earned || 0), login_streak: parseInt(sumR.data.login_streak || 0), referral_count: parseInt(sumR.data.referral_count || 0) });
    if (refR.ok) setReferrals(refR.data.referrals || []);
    setRewardsLoading(false);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const handleWhatsAppShare = () => {
    if (!rewards?.referral_link) return;
    const msg = encodeURIComponent(`Join me on Geilstore Community! 🔥 Use my referral link to sign up and we both earn Gtks: ${rewards.referral_link}`);
    window.open(`https://wa.me/?text=${msg}`, "_blank");
  };

  const handleBuy = async (pkg) => {
    if (!buyPhone.trim()) { flash("Enter your M-Pesa phone number", "error"); return; }
    let phone = buyPhone.trim().replace(/\s/g, "");
    if (phone.startsWith("+254")) phone = phone.replace("+254", "254");
    else if (phone.startsWith("0")) phone = "254" + phone.slice(1);
    else if (!phone.startsWith("254")) phone = "254" + phone;
    if (!/^2547[0-9]{8}$/.test(phone)) { flash("Invalid phone. Use 07XX format", "error"); return; }
    setBuyLoading(true);
    const r = await api("/wallet/buy", { method: "POST", body: JSON.stringify({ package_id: pkg.id, phone }) });
    setBuyLoading(false);
    if (r.ok) {
      setBuySent(true); setCountdown(60);
      const timer = setInterval(() => { setCountdown(p => { if (p <= 1) { clearInterval(timer); setBuySent(false); return 0; } return p - 1; }); }, 1000);
    } else flash(r.message || "Failed", "error");
  };

  const handleSend = async () => {
    if (!sendTo.trim() || !sendAmount) { flash("Enter user ID and amount", "error"); return; }
    setSending(true);
    const r = await api("/wallet/send", { method: "POST", body: JSON.stringify({ user_id: sendTo, amount: parseFloat(sendAmount), message: sendMsg }) });
    setSending(false);
    if (r.ok) { flash(r.data?.message || "Sent! 🪙"); setSendTo(""); setSendAmount(""); setSendMsg(""); loadWallet(); }
    else flash(r.message || "Failed", "error");
  };

  const handleRedeem = async () => {
    if (!redeemTokens || !redeemPhone) { flash("Fill all fields", "error"); return; }
    const r = await api("/wallet/redeem", { method: "POST", body: JSON.stringify({ type: redeemType, tokens: parseFloat(redeemTokens), recipient: redeemPhone, network: redeemNetwork }) });
    if (r.ok) { flash(r.data?.message || "Request submitted! ✅"); setRedeemTokens(""); setRedeemPhone(""); loadWallet(); }
    else flash(r.message || "Failed", "error");
  };

  const txIcon = (type) => {
    const icons = { purchase: "💳", earn: "🎁", send: "📤", receive: "📥", spend: "🛍️", redeem: "🎯", bonus: "🎉", refund: "↩️", admin_gift: "👑" };
    return icons[type] || "🪙";
  };

  const rewardIcon = (type) => {
    const icons = { signup_bonus: "🎉", referral_signup: "🎯", referral_upgrade: "💎", daily_login: "🗓️", viral_post: "🔥", profile_complete: "⭐", tip_received: "💸" };
    return icons[type] || "🪙";
  };

  const txColor = (type) => ["receive","earn","bonus","admin_gift","refund"].includes(type) ? "#10b981" : "#ef4444";
  const statusColor = (status) => ({ signed_up: "#6c5ce7", upgraded_premium: "#a855f7", upgraded_elite: "#f59e0b" }[status] || "#6c5ce7");
  const statusLabel = (status) => ({ signed_up: "Signed Up", upgraded_premium: "Premium ⭐", upgraded_elite: "Elite 👑" }[status] || status);

  if (loading) return (
    <Shell title="Gtks Wallet" showBack>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 0", gap: 10 }}>
        <div style={{ width: 32, height: 32, border: "3px solid " + c.border, borderTopColor: c.pri, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <div style={{ fontSize: 13, color: c.sub }}>Loading wallet...</div>
      </div>
    </Shell>
  );

  return (
    <Shell title="Gtks Wallet" showBack>
      <div style={{ padding: "16px 0" }}>

        {/* Balance Card */}
        <div style={{ background: "linear-gradient(135deg, #6c5ce7 0%, #a855f7 50%, #e91e8c 100%)", borderRadius: 24, padding: "28px 24px", marginBottom: 20, position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: -20, right: -20, width: 120, height: 120, borderRadius: 60, background: "rgba(255,255,255,0.08)" }} />
          <div style={{ position: "absolute", bottom: -30, left: -10, width: 100, height: 100, borderRadius: 50, background: "rgba(255,255,255,0.05)" }} />
          <div style={{ position: "relative", zIndex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.7)", letterSpacing: "0.1em", marginBottom: 8 }}>GTKS BALANCE</div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 48, fontWeight: 800, color: "#fff", lineHeight: 1 }}>{wallet?.balance?.toFixed(0) || 0}</span>
              <span style={{ fontSize: 18, fontWeight: 700, color: "rgba(255,255,255,0.8)" }}>Gtks</span>
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              {[{ label: "Earned", value: wallet?.total_earned?.toFixed(0) || 0, icon: "📈" }, { label: "Spent", value: wallet?.total_spent?.toFixed(0) || 0, icon: "📉" }].map((s, i) => (
                <div key={i} style={{ flex: 1, background: "rgba(255,255,255,0.12)", borderRadius: 12, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", marginBottom: 2 }}>{s.icon} {s.label}</div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#fff" }}>{s.value} Gtks</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Exchange Rate — shown on Redeem tab only via logic below, hidden here */}

        {/* Tabs */}
        <div style={{ display: "flex", gap: 6, marginBottom: 16, overflowX: "auto", scrollbarWidth: "none" }}>
          {[
            { id: "wallet",    label: "💳 Buy" },
            { id: "send",      label: "📤 Send" },
            { id: "redeem",    label: "🎯 Redeem" },
            { id: "rewards",   label: "🏆 Rewards" },
            { id: "referrals", label: "🔗 Refer" },
            { id: "history",   label: "📋 History" },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "8px 16px", borderRadius: 20, border: `1.5px solid ${tab === t.id ? c.pri : c.border}`, background: tab === t.id ? c.priBg : c.card, color: tab === t.id ? c.priTxt : c.sub, fontSize: 12, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0 }}>{t.label}</button>
          ))}
        </div>

        {/* BUY TOKENS TAB — NO KES value shown on packages */}
        {tab === "wallet" && (
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 12 }}>Choose a Package</div>
            {packages.map(pkg => (
              <div key={pkg.id} onClick={() => setBuyingPkg(buyingPkg?.id === pkg.id ? null : pkg)} style={{ background: c.card, borderRadius: 16, border: `${buyingPkg?.id === pkg.id ? "2px" : "1px"} solid ${buyingPkg?.id === pkg.id ? c.pri : pkg.is_popular ? "#a855f7" : c.border}`, padding: "16px", marginBottom: 10, cursor: "pointer", position: "relative" }}>
                {pkg.is_popular && <div style={{ position: "absolute", top: -1, right: 14, padding: "3px 10px", background: "linear-gradient(135deg, #6c5ce7, #a855f7)", borderRadius: "0 0 8px 8px", fontSize: 9, fontWeight: 800, color: "#fff" }}>BEST VALUE</div>}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    {/* Package name — Gtks only, no KES value */}
                    <div style={{ fontSize: 15, fontWeight: 800, color: c.text }}>🪙 {pkg.tokens} Gtks</div>
                    {pkg.bonus_tokens > 0 && <div style={{ fontSize: 11, color: "#10b981", fontWeight: 700 }}>+ {pkg.bonus_tokens} bonus Gtks!</div>}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: c.pri }}>KES {pkg.price_kes}</div>
                  </div>
                </div>
                {buyingPkg?.id === pkg.id && (
                  <div style={{ marginTop: 14, borderTop: `1px solid ${c.border}`, paddingTop: 14 }} onClick={e => e.stopPropagation()}>
                    {buySent ? (
                      <div style={{ background: "#10b98112", borderRadius: 12, padding: "14px", textAlign: "center", border: "1px solid #10b98130" }}>
                        <div style={{ fontSize: 24, marginBottom: 6 }}>📱</div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#10b981", marginBottom: 4 }}>STK Push Sent!</div>
                        <div style={{ fontSize: 11, color: c.sub, marginBottom: 8 }}>Enter your M-Pesa PIN to complete</div>
                        <div style={{ fontSize: 22, fontWeight: 800, color: c.text, marginBottom: 6 }}>{countdown}s</div>
                        <div style={{ height: 3, borderRadius: 2, background: c.border, marginBottom: 10 }}>
                          <div style={{ height: 3, borderRadius: 2, background: "#10b981", width: `${(countdown / 60) * 100}%`, transition: "width 1s" }} />
                        </div>
                        <button onClick={() => setBuySent(false)} style={{ padding: "6px 16px", borderRadius: 8, border: `1px solid ${c.border}`, background: "transparent", color: c.sub, fontSize: 11, cursor: "pointer" }}>Try Again</button>
                      </div>
                    ) : (
                      <>
                        <input style={{ width: "100%", padding: "11px 14px", borderRadius: 11, border: `1.5px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 6 }} placeholder="M-Pesa phone (07XX...)" value={buyPhone} onChange={e => setBuyPhone(e.target.value)} />
                        <div style={{ fontSize: 11, color: c.faint, marginBottom: 10 }}>💡 Works with 07XX or 254XX format</div>
                        <button onClick={() => handleBuy(pkg)} disabled={buyLoading} style={{ width: "100%", padding: "12px", borderRadius: 11, border: "none", background: "linear-gradient(135deg, #6c5ce7, #a855f7)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: buyLoading ? 0.7 : 1 }}>{buyLoading ? "⏳ Sending..." : `💳 Pay KES ${pkg.price_kes}`}</button>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* SEND TOKENS TAB */}
        {tab === "send" && (
          <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.text, marginBottom: 16 }}>📤 Send Gtks</div>

            <div style={{ fontSize: 11, color: c.faint, marginBottom: 6 }}>Recipient</div>
            {selectedRecipient ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 11, border: `1.5px solid ${c.pri}`, background: c.priBg, marginBottom: 16 }}>
                <div style={{ width: 36, height: 36, borderRadius: 18, background: selectedRecipient.avatar_url ? `url(${selectedRecipient.avatar_url}) center/cover` : "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, overflow: "hidden", flexShrink: 0 }}>
                  {!selectedRecipient.avatar_url && (selectedRecipient.emoji || "👤")}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{selectedRecipient.name}</div>
                  {selectedRecipient.verified && <div style={{ fontSize: 10, color: "#3b82f6" }}>✓ Verified</div>}
                </div>
                <button onClick={() => { setSelectedRecipient(null); setSendTo(""); }} style={{ padding: "5px 10px", borderRadius: 8, border: `1px solid ${c.border}`, background: "transparent", color: c.sub, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>✕ Change</button>
              </div>
            ) : (
              <div style={{ marginBottom: 16 }}>
                <input
                  style={{ width: "100%", padding: "11px 14px", borderRadius: 11, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 10 }}
                  placeholder="Search by name..."
                  value={recipientSearch}
                  onChange={e => setRecipientSearch(e.target.value)}
                />

                {recipientSearch.trim() ? (
                  <div>
                    {searchingRecipient && <div style={{ fontSize: 12, color: c.faint, padding: "8px 0" }}>Searching...</div>}
                    {!searchingRecipient && recipientSearchResults.length === 0 && <div style={{ fontSize: 12, color: c.faint, padding: "8px 0" }}>No users found</div>}
                    {recipientSearchResults.map(u => {
                      const isFav = favorites.some(f => f.id === u.id);
                      return (
                        <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 10, cursor: "pointer", marginBottom: 4 }} onClick={() => { setSelectedRecipient({ id: u.id, name: u.name, avatar_url: u.avatar_url, emoji: u.avatar_emoji, verified: u.is_verified, plan: u.plan }); setSendTo(String(u.id)); }}>
                          <div style={{ width: 32, height: 32, borderRadius: 16, background: u.avatar_url ? `url(${u.avatar_url}) center/cover` : "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, overflow: "hidden", flexShrink: 0 }}>{!u.avatar_url && (u.avatar_emoji || "👤")}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{u.name}</div>
                          </div>
                          <button onClick={e => { e.stopPropagation(); toggleFavorite({ id: u.id, name: u.name, avatar_url: u.avatar_url, emoji: u.avatar_emoji, verified: u.is_verified, plan: u.plan }); }} style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer", color: isFav ? "#f59e0b" : c.faint }}>{isFav ? "★" : "☆"}</button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <>
                    {favorites.length > 0 && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: c.sub, marginBottom: 8 }}>⭐ Favorites</div>
                        <div style={{ display: "flex", gap: 10, overflowX: "auto", scrollbarWidth: "none", paddingBottom: 4 }}>
                          {favorites.map(f => (
                            <div key={f.id} onClick={() => { setSelectedRecipient(f); setSendTo(String(f.id)); }} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, cursor: "pointer", flexShrink: 0, width: 56 }}>
                              <div style={{ width: 44, height: 44, borderRadius: 22, background: f.avatar_url ? `url(${f.avatar_url}) center/cover` : "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, overflow: "hidden", border: "2px solid #f59e0b" }}>
                                {!f.avatar_url && (f.emoji || "👤")}
                              </div>
                              <span style={{ fontSize: 10, color: c.sub, textAlign: "center", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", width: "100%" }}>{f.name?.split(" ")[0]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {conversations?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: c.sub, marginBottom: 8 }}>💬 Recent Chats</div>
                        {conversations.slice(0, 5).map(cv => {
                          const isFav = favorites.some(f => f.id === cv.participantId);
                          return (
                            <div key={cv.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 10, cursor: "pointer", marginBottom: 4 }} onClick={() => { setSelectedRecipient({ id: cv.participantId, name: cv.participantName, avatar_url: cv.participantPic, emoji: cv.participantEmoji, verified: cv.participantVerified, plan: cv.participantPlan }); setSendTo(String(cv.participantId)); }}>
                              <div style={{ width: 32, height: 32, borderRadius: 16, background: cv.participantPic ? `url(${cv.participantPic}) center/cover` : "linear-gradient(135deg,#6c5ce7,#e91e8c)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, overflow: "hidden", flexShrink: 0 }}>
                                {!cv.participantPic && (cv.participantEmoji || "👤")}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: c.text }}>{cv.participantName}</div>
                              </div>
                              <button onClick={e => { e.stopPropagation(); toggleFavorite({ id: cv.participantId, name: cv.participantName, avatar_url: cv.participantPic, emoji: cv.participantEmoji, verified: cv.participantVerified, plan: cv.participantPlan }); }} style={{ background: "none", border: "none", fontSize: 16, cursor: "pointer", color: isFav ? "#f59e0b" : c.faint }}>{isFav ? "★" : "☆"}</button>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {favorites.length === 0 && (!conversations || conversations.length === 0) && (
                      <div style={{ fontSize: 12, color: c.faint, textAlign: "center", padding: "16px 0" }}>Search for someone by name to get started</div>
                    )}
                  </>
                )}
              </div>
            )}

            <div style={{ fontSize: 11, color: c.faint, marginBottom: 6 }}>Amount (Gtks)</div>
            <input type="number" style={{ width: "100%", padding: "11px 14px", borderRadius: 11, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 10 }} placeholder="How many Gtks?" value={sendAmount} onChange={e => setSendAmount(e.target.value)} />
            <div style={{ fontSize: 11, color: c.faint, marginBottom: 6 }}>Message (optional)</div>
            <input style={{ width: "100%", padding: "11px 14px", borderRadius: 11, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 16 }} placeholder="Add a note..." value={sendMsg} onChange={e => setSendMsg(e.target.value)} />
            {sendAmount && wallet?.balance < parseFloat(sendAmount) && <div style={{ fontSize: 12, color: "#ef4444", marginBottom: 10, fontWeight: 600 }}>⚠️ Insufficient balance</div>}
            <button onClick={handleSend} disabled={sending || !sendTo || !sendAmount || wallet?.balance < parseFloat(sendAmount || 0)} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #6c5ce7, #a855f7)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", opacity: sending ? 0.7 : 1 }}>{sending ? "Sending..." : `🪙 Send ${sendAmount || 0} Gtks`}</button>
            <div style={{ marginTop: 14, padding: "12px 14px", borderRadius: 11, background: c.input, border: `1px solid ${c.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: c.sub, marginBottom: 4 }}>💡 What can you send Gtks for?</div>
              <div style={{ fontSize: 11, color: c.faint, lineHeight: 1.6 }}>Tip a post creator · Send a gift · Make an offer · Pay for a service · Lunch dates 🍽️</div>
            </div>
          </div>
        )}

        {/* REDEEM TAB — KES value shown ONLY for M-Pesa Cash AND only when typing */}
        {tab === "redeem" && (
          <div style={{ background: c.card, borderRadius: 16, border: `1px solid ${c.border}`, padding: 18 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: c.text, marginBottom: 4 }}>🎯 Redeem Gtks</div>
            <div style={{ fontSize: 12, color: c.faint, marginBottom: 16 }}>Min 500 Gtks · 10% platform fee · Processed within 24hrs</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
              {[
                { id: "airtime",   icon: "📱", label: "Airtime",          desc: "Any network" },
                { id: "data",      icon: "🌐", label: "Data Bundle",       desc: "Any network" },
                { id: "mpesa",     icon: "💚", label: "M-Pesa Cash",       desc: "To your phone" },
                { id: "geilstore", icon: "🛍️", label: "Geilstore Voucher", desc: "Shop discount" },
              ].map(rt => (
                <button key={rt.id} onClick={() => setRedeemType(rt.id)} style={{ padding: "12px 10px", borderRadius: 12, border: `2px solid ${redeemType === rt.id ? c.pri : c.border}`, background: redeemType === rt.id ? c.priBg : c.card, cursor: "pointer", textAlign: "center" }}>
                  <div style={{ fontSize: 22, marginBottom: 4 }}>{rt.icon}</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: redeemType === rt.id ? c.priTxt : c.text }}>{rt.label}</div>
                  <div style={{ fontSize: 10, color: c.faint }}>{rt.desc}</div>
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: c.faint, marginBottom: 6 }}>Gtks to redeem (min 500)</div>
            <input type="number" style={{ width: "100%", padding: "11px 14px", borderRadius: 11, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 6 }} placeholder="How many Gtks?" value={redeemTokens} onChange={e => setRedeemTokens(e.target.value)} min="500" />
            {/* KES value ONLY shown when: type is mpesa AND tokens are typed */}
            {redeemType === "mpesa" && redeemTokens && parseFloat(redeemTokens) >= 500 && (
              <div style={{ fontSize: 13, color: "#10b981", fontWeight: 700, marginBottom: 10, padding: "8px 12px", background: "#10b98112", borderRadius: 8, border: "1px solid #10b98130" }}>
                💚 You'll receive: KES {(parseFloat(redeemTokens || 0) * 0.9).toFixed(0)} via M-Pesa
              </div>
            )}
            {(redeemType === "airtime" || redeemType === "data" || redeemType === "mpesa") && (
              <>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 6 }}>Phone number</div>
                <input style={{ width: "100%", padding: "11px 14px", borderRadius: 11, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 10 }} placeholder="07XX XXX XXX" value={redeemPhone} onChange={e => setRedeemPhone(e.target.value)} />
              </>
            )}
            {(redeemType === "airtime" || redeemType === "data") && (
              <>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 6 }}>Network</div>
                <select style={{ width: "100%", padding: "11px 14px", borderRadius: 11, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", marginBottom: 16 }} value={redeemNetwork} onChange={e => setRedeemNetwork(e.target.value)}>
                  {["Safaricom","Airtel","Telkom"].map(n => <option key={n}>{n}</option>)}
                </select>
              </>
            )}
            {redeemType === "geilstore" && (
              <>
                <div style={{ fontSize: 11, color: c.faint, marginBottom: 6 }}>Email for voucher</div>
                <input style={{ width: "100%", padding: "11px 14px", borderRadius: 11, border: `1px solid ${c.border}`, background: c.input, color: c.text, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", marginBottom: 16 }} placeholder="your@email.com" value={redeemPhone} onChange={e => setRedeemPhone(e.target.value)} />
              </>
            )}
            <button onClick={handleRedeem} disabled={!redeemTokens || parseFloat(redeemTokens) < 500 || !redeemPhone || (wallet?.balance || 0) < parseFloat(redeemTokens || 0)} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #10b981, #059669)", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer" }}>🎯 Redeem {redeemTokens || 0} Gtks</button>
            <div style={{ marginTop: 12, fontSize: 11, color: c.faint, textAlign: "center" }}>⏱️ Processed within 24 hours</div>
          </div>
        )}

        {/* REWARDS TAB */}
        {tab === "rewards" && (
          <div>
            {rewardsLoading ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: c.faint }}>Loading rewards...</div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                  {[
                    { label: "Total Earned",   value: `${rewards?.total_earned?.toFixed(0) || 0} Gtks`, icon: "🏆", color: "#f59e0b" },
                    { label: "Login Streak",    value: `${rewards?.login_streak || 0} days`,            icon: "🔥", color: "#ef4444" },
                    { label: "Balance",         value: `${rewards?.gt_balance?.toFixed(0) || 0} Gtks`,  icon: "🪙", color: "#6c5ce7" },
                    { label: "From Referrals",  value: `${rewards?.referral_earned?.toFixed(0) || 0} Gtks`, icon: "🎯", color: "#10b981" },
                  ].map((s, i) => (
                    <div key={i} style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: "14px 16px" }}>
                      <div style={{ fontSize: 22, marginBottom: 6 }}>{s.icon}</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                      <div style={{ fontSize: 11, color: c.faint, marginTop: 2 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: c.text, marginBottom: 12 }}>💡 Ways to Earn More Gtks</div>
                  {[
                    { icon: "🗓️", action: "Login daily",              reward: "+5 Gtks/day" },
                    { icon: "🔥", action: "7-day streak bonus",        reward: "+50 Gtks" },
                    { icon: "🎯", action: "Refer a friend (signup)",   reward: "+50 Gtks" },
                    { icon: "💎", action: "Refer → Premium upgrade",   reward: "+100 Gtks" },
                    { icon: "👑", action: "Refer → Elite upgrade",     reward: "+200 Gtks" },
                    { icon: "🚀", action: "Post goes viral (50 likes)", reward: "+20 Gtks" },
                    { icon: "⭐", action: "Complete your profile",      reward: "+25 Gtks" },
                  ].map((e, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: i < 6 ? 10 : 0, marginBottom: i < 6 ? 10 : 0, borderBottom: i < 6 ? `1px solid ${c.border}` : "none" }}>
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <span style={{ fontSize: 18 }}>{e.icon}</span>
                        <span style={{ fontSize: 13, color: c.text }}>{e.action}</span>
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 800, color: "#10b981" }}>{e.reward}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 10 }}>Recent Rewards</div>
                {rewardHistory.length === 0 ? (
                  <EmptyState icon="🏆" title="No rewards yet" desc="Start earning by logging in daily and referring friends!" />
                ) : rewardHistory.map((log) => (
                  <div key={log.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, marginBottom: 8 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 21, background: "#10b98115", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{rewardIcon(log.type)}</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: c.text, marginBottom: 2 }}>{log.description}</div>
                      <div style={{ fontSize: 11, color: c.faint }}>{new Date(log.created_at).toLocaleDateString()}</div>
                    </div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "#10b981", flexShrink: 0 }}>+{log.gt_amount} Gtks</div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* REFERRALS TAB */}
        {tab === "referrals" && (
          <div>
            {rewardsLoading ? (
              <div style={{ textAlign: "center", padding: "40px 0", color: c.faint }}>Loading...</div>
            ) : (
              <>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                  <div style={{ background: "linear-gradient(135deg, #6c5ce7, #a855f7)", borderRadius: 14, padding: "16px", textAlign: "center" }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>{rewards?.referral_count || 0}</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>Friends Referred</div>
                  </div>
                  <div style={{ background: "linear-gradient(135deg, #10b981, #059669)", borderRadius: 14, padding: "16px", textAlign: "center" }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: "#fff" }}>{rewards?.referral_earned?.toFixed(0) || 0} Gtks</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,0.8)" }}>Gtks Earned</div>
                  </div>
                </div>
                <div style={{ background: c.card, borderRadius: 16, border: `1.5px solid ${c.pri}40`, padding: "18px", marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: c.text, marginBottom: 14 }}>🔗 Your Referral Code</div>
                  <div style={{ background: dark ? "#1a1a2e" : "#f3f0ff", borderRadius: 12, padding: "14px 16px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 10, color: c.faint, fontWeight: 700, marginBottom: 4 }}>REFERRAL CODE</div>
                      <div style={{ fontSize: 22, fontWeight: 900, color: c.pri, letterSpacing: 3 }}>{rewards?.referral_code || "..."}</div>
                    </div>
                    <button onClick={() => handleCopy(rewards?.referral_code || "")} style={{ padding: "8px 14px", borderRadius: 10, border: `1px solid ${c.pri}`, background: "transparent", color: c.pri, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{copied ? "✅ Copied!" : "📋 Copy"}</button>
                  </div>
                  <div style={{ background: dark ? "#1a1a2e" : "#f3f0ff", borderRadius: 12, padding: "12px 16px", marginBottom: 14 }}>
                    <div style={{ fontSize: 10, color: c.faint, fontWeight: 700, marginBottom: 4 }}>REFERRAL LINK</div>
                    <div style={{ fontSize: 11, color: c.text, wordBreak: "break-all", marginBottom: 8 }}>{rewards?.referral_link || "..."}</div>
                    <button onClick={() => handleCopy(rewards?.referral_link || "")} style={{ padding: "6px 14px", borderRadius: 8, border: `1px solid ${c.border}`, background: "transparent", color: c.sub, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>📋 Copy Link</button>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={handleWhatsAppShare} style={{ flex: 1, padding: "12px", borderRadius: 12, border: "none", background: "#25D366", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>📲 Share on WhatsApp</button>
                    <button onClick={() => handleCopy(rewards?.referral_link || "")} style={{ padding: "12px 16px", borderRadius: 12, border: `1px solid ${c.border}`, background: c.card, color: c.text, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>🔗</button>
                  </div>
                </div>
                <div style={{ background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: c.text, marginBottom: 12 }}>💰 Referral Earnings</div>
                  {[
                    { label: "Friend signs up",          reward: "50 Gtks",  color: "#6c5ce7" },
                    { label: "Friend upgrades Premium",  reward: "100 Gtks", color: "#a855f7" },
                    { label: "Friend upgrades Elite",    reward: "200 Gtks", color: "#f59e0b" },
                  ].map((e, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", paddingBottom: i < 2 ? 10 : 0, marginBottom: i < 2 ? 10 : 0, borderBottom: i < 2 ? `1px solid ${c.border}` : "none" }}>
                      <span style={{ fontSize: 13, color: c.text }}>{e.label}</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: e.color }}>+{e.reward}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 10 }}>Your Referrals ({referrals.length})</div>
                {referrals.length === 0 ? (
                  <EmptyState icon="🎯" title="No referrals yet" desc="Share your link and start earning!" />
                ) : referrals.map((ref, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, marginBottom: 8 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 21, background: c.priBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>👤</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: c.text }}>{ref.user?.name || "Unknown"}</div>
                      <div style={{ fontSize: 11, color: c.faint }}>{ref.joined_at}</div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: statusColor(ref.status), marginBottom: 2 }}>{statusLabel(ref.status)}</div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#10b981" }}>+{ref.gt_awarded} Gtks</div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* HISTORY TAB */}
        {tab === "history" && (
          <div>
            {transactions.length === 0 ? (
              <EmptyState icon="🪙" title="No transactions yet" desc="Buy Gtks to get started!" />
            ) : transactions.map((tx) => (
              <div key={tx.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "13px 14px", background: c.card, borderRadius: 14, border: `1px solid ${c.border}`, marginBottom: 8 }}>
                <div style={{ width: 44, height: 44, borderRadius: 22, background: txColor(tx.type) + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>{txIcon(tx.type)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: c.text, marginBottom: 2 }}>{tx.description || tx.type}</div>
                  <div style={{ fontSize: 11, color: c.faint }}>{new Date(tx.created_at).toLocaleDateString()} · Balance: {tx.balance_after} Gtks · ID #{tx.id}</div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 800, color: txColor(tx.type) }}>{["receive","earn","bonus","admin_gift","refund"].includes(tx.type) ? "+" : "-"}{tx.amount} Gtks</div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: tx.status === "completed" ? "#10b981" : tx.status === "pending" ? "#f59e0b" : "#ef4444" }}>{tx.status}</div>
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </Shell>
  );
};

export default WalletPage;